import { StyleSheet, css } from 'aphrodite';
import { type FC, type MouseEvent, useState } from 'react';
import { Button } from '../../../web/components-v2/Button';
import type { KeywordPin } from '../pins';
import { IconEdit, IconSend, IconTrash } from './icons';

/**
 * A conversation-starter row in the right rail.
 *
 * Similar visual treatment to `KeywordPinRow`, but the row is tap-to-send:
 * clicking anywhere in the body (excluding the hover edit/delete actions)
 * fires `onSend`. The right-side send chip also triggers send; it lights up
 * to the pin's color on hover as a "ready to fire" affordance.
 *
 * Matches `/tmp/design-bundle/focused-view/project/RightColumn.jsx`
 * (`StarterRow`).
 */
type StarterRowProps = {
	pin: KeywordPin;
	onSend: () => void;
	/** Optional. When omitted, the edit affordance is hidden — the call surface
	 * uses tap-to-send rows without edit/delete clutter, while the pin
	 * configuration panel passes both handlers to keep the management UX. */
	onEdit?: () => void;
	/** Optional. See `onEdit`. Hides the delete affordance when omitted. */
	onDelete?: () => void;
};

export const StarterRow: FC<StarterRowProps> = ({ pin, onSend, onEdit, onDelete }) => {
	const [hovered, setHovered] = useState(false);

	const handleEditClick = (e: MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		onEdit?.();
	};

	const handleDeleteClick = (e: MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		onDelete?.();
	};

	const handleSendClick = (e: MouseEvent<HTMLButtonElement>) => {
		e.stopPropagation();
		onSend();
	};

	const showActions = Boolean(onEdit) || Boolean(onDelete && !pin.builtin);

	return (
		<div
			onMouseEnter={() => setHovered(true)}
			onMouseLeave={() => setHovered(false)}
			className={css(styles.row)}
			style={{ borderLeftColor: pin.color }}
		>
			<div className={css(styles.main)}>
				<div className={css(styles.label)}>{pin.label}</div>
				<div className={css(styles.keyword)}>&quot;{pin.keyword}&quot;</div>
			</div>

			{showActions && (
				<div className={css(styles.actions)} style={{ opacity: hovered ? 1 : 0 }}>
					{onEdit && (
						<Button variant='ghost' size='icon' title='Edit' onClick={handleEditClick}>
							<IconEdit size={14} />
						</Button>
					)}
					{onDelete && !pin.builtin && (
						<Button variant='ghost' size='icon' title='Delete' onClick={handleDeleteClick}>
							<IconTrash size={14} />
						</Button>
					)}
				</div>
			)}

			<Button variant='secondary' size='sm' title='Send' onClick={handleSendClick}>
				<IconSend size={13} sw={2} />
				Send
			</Button>
		</div>
	);
};

const styles = StyleSheet.create({
	row: {
		display: 'flex',
		alignItems: 'center',
		gap: 10,
		padding: '9px 10px 9px 12px',
		background: 'var(--bg-elev)',
		border: '1px solid var(--border)',
		borderLeft: '3px solid var(--border-strong)',
		borderRadius: 8,
		transition: 'background 120ms',
		textAlign: 'left',
	},
	main: {
		flex: 1,
		minWidth: 0,
	},
	label: {
		fontSize: 12.5,
		color: 'var(--ink)',
		fontWeight: 500,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	keyword: {
		fontSize: 10.5,
		color: 'var(--ink-muted)',
		marginTop: 2,
		fontFamily: 'var(--font-mono)',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
	},
	actions: {
		display: 'flex',
		alignItems: 'center',
		gap: 2,
		transition: 'opacity 120ms',
	},
});
